import { getApiBaseUrl } from '../../utils/apiUrl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

interface MarkdownMessageProps {
  content: string;
  isUser?: boolean;
  fileIds?: string[]; // Optional file IDs from message metadata
  imageFileIds?: string[]; // Optional image file IDs from message metadata
  attachmentFilenames?: string[]; // Optional attachment filenames for sandbox-image endpoint
  conversationId?: string; // Optional conversation ID for sandbox-image endpoint
  csvFileId?: string; // Exclude from image sources - CSV is not an image
  stripGraphImages?: boolean; // When true, remove ![alt](sandbox:/mnt/data/*.png) from content to avoid duplicate display (graph block in ChatInterface handles it)
}

// Styled markdown container with beautiful typography
const StyledMarkdown = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  // Prevent overflow and ensure proper word wrapping
  overflow: 'hidden',
  wordWrap: 'break-word',
  overflowWrap: 'anywhere',
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  
  '& h1': {
    fontSize: '1.5rem',
    borderBottom: `2px solid ${isUser ? 'rgba(255,255,255,0.3)' : theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
  },
  
  '& h2': {
    fontSize: '1.3rem',
  },
  
  '& h3': {
    fontSize: '1.1rem',
  },
  
  '& p': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  
  '& ul, & ol': {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  
  '& li': {
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.5,
    '& p': {
      marginBottom: theme.spacing(0.5),
    },
  },
  
  '& blockquote': {
    borderLeft: `4px solid ${isUser ? 'rgba(255,255,255,0.4)' : theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    margin: `${theme.spacing(1)} 0`,
    fontStyle: 'italic',
    backgroundColor: isUser 
      ? 'rgba(255,255,255,0.1)' 
      : theme.palette.grey[50],
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
  },
  
  '& code': {
    backgroundColor: isUser 
      ? 'rgba(255,255,255,0.2)' 
      : theme.palette.grey[100],
    color: isUser 
      ? theme.palette.primary.contrastText 
      : theme.palette.text.primary,
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '0.875rem',
    fontFamily: '"Fira Code", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    border: isUser 
      ? '1px solid rgba(255,255,255,0.3)' 
      : `1px solid ${theme.palette.grey[300]}`,
  },
  
  '& pre': {
    backgroundColor: isUser 
      ? 'rgba(0,0,0,0.3)' 
      : theme.palette.grey[100],
    color: isUser 
      ? theme.palette.primary.contrastText 
      : theme.palette.text.primary,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    overflowX: 'auto',
    overflowY: 'hidden',
    margin: `${theme.spacing(1)} 0`,
    border: isUser 
      ? '1px solid rgba(255,255,255,0.2)' 
      : `1px solid ${theme.palette.grey[300]}`,
    maxWidth: '100%',
    boxSizing: 'border-box',
    
    // Custom scrollbar for code blocks
    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: isUser ? 'rgba(255,255,255,0.1)' : theme.palette.grey[200],
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb': {
      background: isUser ? 'rgba(255,255,255,0.3)' : theme.palette.grey[400],
      borderRadius: 3,
      '&:hover': {
        background: isUser ? 'rgba(255,255,255,0.5)' : theme.palette.grey[500],
      },
    },
    
    '& code': {
      backgroundColor: 'transparent',
      color: 'inherit',
      padding: 0,
      border: 'none',
      fontSize: '0.875rem',
      fontFamily: '"Fira Code", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      lineHeight: 1.4,
      whiteSpace: 'pre', // Preserve formatting but allow horizontal scroll
    },
  },
  
  // Table container with horizontal scroll
  '& .table-container': {
    overflowX: 'auto',
    margin: `${theme.spacing(1)} 0`,
    border: `1px solid ${isUser ? 'rgba(255,255,255,0.3)' : theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    
    // Custom scrollbar styling
    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: isUser ? 'rgba(255,255,255,0.1)' : theme.palette.grey[100],
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb': {
      background: isUser ? 'rgba(255,255,255,0.3)' : theme.palette.grey[400],
      borderRadius: 3,
      '&:hover': {
        background: isUser ? 'rgba(255,255,255,0.5)' : theme.palette.grey[500],
      },
    },
  },

  '& table': {
    minWidth: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
    
    '& th, & td': {
      padding: theme.spacing(1),
      textAlign: 'left',
      borderBottom: `1px solid ${
        isUser ? 'rgba(255,255,255,0.2)' : theme.palette.grey[200]
      }`,
      borderRight: `1px solid ${
        isUser ? 'rgba(255,255,255,0.1)' : theme.palette.grey[100]
      }`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: '80px', // Minimum column width
      maxWidth: '200px', // Maximum column width to prevent huge cells
      
      '&:last-child': {
        borderRight: 'none',
      },
      
      // Show full text on hover with tooltip-like behavior
      '&:hover': {
        position: 'relative',
        overflow: 'visible',
        zIndex: 10,
        
        '&::after': {
          content: 'attr(title)',
          position: 'absolute',
          top: '100%',
          left: 0,
          backgroundColor: theme.palette.grey[900],
          color: 'white',
          padding: theme.spacing(0.5, 1),
          borderRadius: theme.shape.borderRadius,
          fontSize: '0.75rem',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          maxWidth: '300px',
          zIndex: 1000,
          boxShadow: theme.shadows[4],
          display: 'var(--show-tooltip, none)',
        },
      },
    },
    
    '& th': {
      fontWeight: 600,
      backgroundColor: isUser 
        ? 'rgba(255,255,255,0.15)' 
        : theme.palette.grey[100],
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    
    '& tbody tr:hover': {
      backgroundColor: isUser 
        ? 'rgba(255,255,255,0.08)' 
        : theme.palette.grey[50],
    },
    
    // Responsive column sizing
    '& th:first-of-type, & td:first-of-type': {
      minWidth: '120px', // First column gets more space
    },
  },
  
  '& a': {
    color: isUser 
      ? theme.palette.primary.contrastText 
      : theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    borderBottom: `1px solid ${
      isUser 
        ? 'rgba(255,255,255,0.5)' 
        : theme.palette.primary.main
    }`,
    transition: 'all 0.2s ease',
    
    '&:hover': {
      opacity: 0.8,
      borderBottomWidth: 2,
    },
  },
  
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${
      isUser ? 'rgba(255,255,255,0.3)' : theme.palette.grey[300]
    }`,
    margin: `${theme.spacing(2)} 0`,
  },
  
  '& strong, & b': {
    fontWeight: 600,
    color: isUser 
      ? theme.palette.primary.contrastText 
      : theme.palette.text.primary,
  },
  
  '& em, & i': {
    fontStyle: 'italic',
    color: isUser 
      ? 'rgba(255,255,255,0.9)' 
      : theme.palette.text.secondary,
  },

  // Special styling for checkboxes in task lists
  '& input[type="checkbox"]': {
    marginRight: theme.spacing(1),
  },
  
  // Improved spacing for nested lists
  '& ul ul, & ol ol, & ul ol, & ol ul': {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
}));

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content, isUser = false, fileIds = [], imageFileIds = [], attachmentFilenames = [], conversationId, csvFileId, stripGraphImages = false }) => {
  // Use fileIds from metadata (validated by backend) as primary source
  // Also extract file_ids from text as fallback (backend will validate on download)
  // This allows links to be created even if backend validation happens after message is sent
  const fileIdPattern = /file-[A-Za-z0-9]{20,}/g;
  const extractedFileIds = content.match(fileIdPattern) || [];
  
  // Combine: prefer validated fileIds from metadata, fallback to extracted ones
  // Backend will validate on download attempt
  const allFileIds = Array.from(new Set([...fileIds, ...extractedFileIds]));
  
  // Image file IDs from metadata (for attachment:filename.png format)
  const allImageFileIds = Array.from(new Set([...imageFileIds]));
  
  // For image resolution: exclude CSV file - it's never an image, using it causes "failed to load"
  const imageEligibleFileIds = csvFileId 
    ? allFileIds.filter((id) => id !== csvFileId) 
    : allFileIds;
  
  // Attachment filenames from metadata (for sandbox-image endpoint)
  const allAttachmentFilenames = Array.from(new Set([...attachmentFilenames]));
  
  // Mask CSV content between [CSV_CONTENT_START] and [CSV_CONTENT_END]
  // The backend needs this content to create the downloadable file, but we hide it completely from display
  let processedContent = content;
  
  // Completely remove the CSV section including markers (backend will still detect it from original content in DB)
  processedContent = processedContent.replace(
    /\[CSV_CONTENT_START\][\s\S]*?\[CSV_CONTENT_END\]/g,
    () => {
      // Remove completely - return empty string
      return '';
    }
  );
  
  // Also mask image content between [IMAGE_CONTENT_START] and [IMAGE_CONTENT_END] (base64 encoded images)
  processedContent = processedContent.replace(
    /\[IMAGE_CONTENT_START\][\s\S]*?\[IMAGE_CONTENT_END\]/g,
    () => {
      // Remove completely - return empty string
      return '';
    }
  );
  
  // Note: We no longer mask attachment: references - let them render and use sandbox-image endpoint
  // The img handler below will convert attachment: URLs to sandbox-image API calls
  // This allows images to display even when Code Interpreter generates them as attachment: references

  // When stripGraphImages=true: ChatInterface displays graphs in a dedicated block - remove markdown images to avoid duplicate display
  if (stripGraphImages) {
    processedContent = processedContent.replace(
      /!\[[^\]]*\]\((?:sandbox:)?\/mnt\/data\/[a-zA-Z0-9_\-]+\.(?:png|jpg|jpeg)\)/gi,
      ''
    ).replace(
      /!\[[^\]]*\]\(attachment:[a-zA-Z0-9_\-]+\.(?:png|jpg|jpeg)\)/gi,
      ''
    );
    processedContent = processedContent.replace(/\n{3,}/g, '\n\n').trim();
  }
  
  // Only show "📁 Fichier CSV disponible" line if we have file_ids (message is complete)
  // This prevents showing the filename before the file is ready for download
  if (allFileIds.length === 0) {
    // Mask the "📁 Fichier CSV disponible" line if no file_ids yet
    processedContent = processedContent.replace(/📁\s*Fichier\s+CSV\s+disponible[^\n]*/gi, '');
  }

  // Convert sandbox links to working download URLs (sandbox-file fetches from OpenAI at request time - reliable on Cloud Run)
  // Do NOT remove - keep as fallback when /api/files/{file_id} fails (ephemeral storage)
  const sandboxCsvLinkRegex = /\[(Télécharger[^\]]*selection\.csv)\]\((sandbox:\/mnt\/data\/[^)]+|\/mnt\/data\/[^)]+)\)/gi;
  processedContent = processedContent.replace(sandboxCsvLinkRegex, (_, linkText, _sandboxPath) => {
    const filename = 'selection.csv';
    const url = conversationId
      ? `${getApiBaseUrl()}/api/files/sandbox-file/${encodeURIComponent(filename)}?conversation_id=${encodeURIComponent(conversationId)}`
      : allFileIds.length > 0
        ? `${getApiBaseUrl()}/api/files/${allFileIds[0]}`
        : '#';
    return `[${linkText}](${url})`;
  });

  // When we have fileIds, also remove duplicate diagnostic lines
  if (allFileIds.length > 0) {
    // Remove plain "Télécharger le fichier: X" or "Télécharger X.csv" lines (already converted above)
    processedContent = processedContent.replace(/^Télécharger\s+(?:le\s+fichier:\s*)?[^\n]+\.csv\s*$/gim, '');
    // Remove diagnostic lines: internal file-xxx IDs, or "Il semble que le fichier..." (technical jargon)
    processedContent = processedContent.replace(
      /^[^\n]*(?:file-[A-Za-z0-9]{20,}|Il semble que le fichier[^\n]*(?:n'est pas nommé|sans .csv|porte un nom))[^\n]*$/gim,
      ''
    );
    // Strip remaining (file-xxx) references so we don't render them as links (dedicated section handles download)
    processedContent = processedContent.replace(/\(file-[A-Za-z0-9]{20,}\)/g, '');
  }
  
  // Clean up any extra newlines left after removal
  processedContent = processedContent.replace(/\n{3,}/g, '\n\n').trim();
  
  // Convert file IDs in text to markdown links
  // ReactMarkdown will parse these and use our custom 'a' component
  if (allFileIds.length > 0) {
    const fileId = allFileIds[0]; // Use first file ID
    const downloadUrl = `${getApiBaseUrl()}/api/files/${fileId}`;
    
    // Pattern 1: "🔗 Télécharger le fichier (file-XXXXX)" -> convert to markdown link
    processedContent = processedContent.replace(
      /(🔗\s*Télécharger\s+le\s+fichier\s*\(?)(file-[A-Za-z0-9]{20,})(\)?)/gi,
      (match) => {
        // If already a markdown link, skip
        if (match.includes('[') && match.includes('](')) {
          return match;
        }
        // Convert to markdown link - ReactMarkdown will parse this correctly
        return `[🔗 Télécharger le fichier](${downloadUrl})`;
      }
    );
    
    // Pattern 2: Standalone "(file-XXXXX)" -> convert to markdown link
    processedContent = processedContent.replace(
      /\(file-([A-Za-z0-9]{20,})\)/g,
      (match, fileIdMatch, offset, fullString) => {
        // Check if already in a markdown link
        const before = fullString.substring(Math.max(0, offset - 50), offset);
        const after = fullString.substring(offset + match.length, Math.min(fullString.length, offset + match.length + 50));
        
        // Skip if already in a markdown link
        if ((before.includes('[') && after.includes('](')) || 
            (before.match(/\[.*\]\(/) && after.match(/\)/))) {
          return match;
        }
        // Return markdown link WITHOUT outer parentheses
        return `[file-${fileIdMatch}](${downloadUrl})`;
      }
    );
    
    // Pattern 3: Standalone "file-XXXXX" (not in parentheses or already linked)
    processedContent = processedContent.replace(
      /\b(file-[A-Za-z0-9]{20,})\b/g,
      (match, _fileIdMatch, offset, fullString) => {
        // Get context around the match
        const before = fullString.substring(Math.max(0, offset - 50), offset);
        const after = fullString.substring(offset + match.length, Math.min(fullString.length, offset + match.length + 50));
        
        // Skip if already in parentheses (Pattern 2 will handle it)
        if (before.includes('(') && after.includes(')')) {
          return match;
        }
        // Skip if already in a markdown link
        if ((before.includes('[') && after.includes('](')) || 
            (before.match(/\[.*\]\(/) && after.match(/\)/))) {
          return match;
        }
        // Skip if immediately after "🔗 Télécharger le fichier" (Pattern 1 handles it)
        if (before.includes('🔗') && before.includes('Télécharger')) {
          return match;
        }
        // Convert to markdown link
        return `[${match}](${downloadUrl})`;
      }
    );
  }
  
  // Convert sandbox links to download URLs
  const convertSandboxLinks = (href: string): string => {
    if (href.startsWith('sandbox:/mnt/data/') || href.startsWith('/mnt/data/')) {
      // If we have file IDs, use the first one
      if (allFileIds.length > 0) {
        return `${getApiBaseUrl()}/api/files/${allFileIds[0]}`;
      }
      // Fallback: use sandbox-file endpoint (fetches from Containers API when csv_file_ready didn't fire)
      if (conversationId) {
        const filename = href.split('/').pop() || 'selection.csv';
        return `${getApiBaseUrl()}/api/files/sandbox-file/${encodeURIComponent(filename)}?conversation_id=${conversationId}`;
      }
      console.warn('⚠️ Sandbox link found but no file IDs or conversationId:', href);
      return '#';
    }
    return href;
  };

  return (
    <StyledMarkdown isUser={isUser}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom link component to handle sandbox:// URLs and file downloads
          a: ({ href, children, ...props }: any) => {
            // CRITICAL: Decode URL first, then extract URL from markdown format if needed
            // ReactMarkdown sometimes passes [text](url) as href, and it may be URL-encoded
            let actualHref = href;
            
            // Step 1: Decode URL encoding (%5B = [, %5D = ])
            try {
              actualHref = decodeURIComponent(href || '');
            } catch (e) {
              // If decoding fails, use original href
              actualHref = href;
            }
            
            // Step 2: Extract URL from markdown format [text](url) if present
            if (actualHref && actualHref.includes('[') && actualHref.includes('](')) {
              // Try to extract URL from markdown format [text](url)
              // Match both HTTP/HTTPS URLs and sandbox URLs
              const markdownLinkMatch = actualHref.match(/\[.*?\]\((sandbox:[^)]+|https?:\/\/[^)]+|\/mnt\/data\/[^)]+)\)/);
              if (markdownLinkMatch && markdownLinkMatch[1]) {
                actualHref = markdownLinkMatch[1];
                console.log('🔧 Extracted URL from markdown link:', actualHref);
              }
            }
            
            const convertedHref = actualHref ? convertSandboxLinks(actualHref) : '#';
            const isSandboxLink = actualHref?.startsWith('sandbox:') || actualHref?.startsWith('/mnt/data/');
            
            // Check if this is a file download link (href contains /api/files/)
            const isFileDownloadLink = actualHref?.includes('/api/files/');
            
            // If we have file IDs and href is empty/invalid, treat as download link
            const hasFileIds = allFileIds.length > 0;
            const isDownloadLink = isSandboxLink || isFileDownloadLink || (hasFileIds && (!actualHref || actualHref === '#' || convertedHref === '#'));
            
            // Get download URL
            let downloadUrl = convertedHref;
            if (isFileDownloadLink && actualHref) {
              downloadUrl = actualHref; // Use the extracted URL directly
            } else if (isDownloadLink && hasFileIds) {
              downloadUrl = `${getApiBaseUrl()}/api/files/${allFileIds[0]}`;
            } else if (actualHref && actualHref.includes('/api/files/')) {
              downloadUrl = actualHref;
            } else if (convertedHref && convertedHref !== '#') {
              downloadUrl = convertedHref;
            }
            
            console.log('🔗 Link component - href:', href, 'decoded:', actualHref, 'downloadUrl:', downloadUrl, 'isFileDownloadLink:', isFileDownloadLink);
            
            return (
              <a
                {...props}
                href={downloadUrl}
                onClick={(e) => {
                  if (isDownloadLink && downloadUrl !== '#') {
                    e.preventDefault();
                    console.log('🔽 Downloading file from:', downloadUrl);
                    // Trigger download
                    fetch(downloadUrl, {
                      credentials: 'include',
                      headers: { 'Accept': '*/*' }
                    })
                      .then(response => {
                        if (!response.ok) throw new Error(`HTTP ${response.status}`);
                        
                        // Extract filename from Content-Disposition
                        const contentDisposition = response.headers.get('content-disposition');
                        let filename = 'download';
                        
                        if (contentDisposition) {
                          const match = contentDisposition.match(/filename="?([^"]+)"?/);
                          if (match?.[1]) {
                            filename = match[1].replace(/^\/mnt\/data\//, '');
                          }
                        }
                        
                        return response.blob().then(blob => ({ blob, filename }));
                      })
                      .then(({ blob, filename }) => {
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        console.log('✅ File downloaded successfully:', filename);
                      })
                      .catch(error => {
                        console.error('❌ Markdown link download failed:', error);
                        window.open(downloadUrl, '_blank');
                      });
                  }
                }}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'inherit',
                }}
              >
                {children}
              </a>
            );
          },
          
          // Custom table component with horizontal scrolling
          table: ({ children, ...props }) => (
            <Box className="table-container">
              <table {...props}>{children}</table>
            </Box>
          ),
          
          // Custom table cells with title attributes for tooltips
          td: ({ children, ...props }) => (
            <td 
              {...props} 
              title={typeof children === 'string' ? children : children?.toString() || ''}
              style={{ 
                ...props.style, 
                '--show-tooltip': typeof children === 'string' && children.length > 30 ? 'block' : 'none' 
              } as React.CSSProperties}
            >
              {children}
            </td>
          ),
          
          th: ({ children, ...props }) => (
            <th 
              {...props}
              title={typeof children === 'string' ? children : children?.toString() || ''}
              style={{ 
                ...props.style, 
                '--show-tooltip': typeof children === 'string' && children.length > 30 ? 'block' : 'none' 
              } as React.CSSProperties}
            >
              {children}
            </th>
          ),
          
          // Custom image component with responsive sizing and sandbox URL conversion
          img: ({ src, alt, ...props }: any) => {
            // Convert sandbox URLs and attachment: URLs to API URLs
            let imgSrc = src;
            
            // NEW APPROACH: Priority order for attachment: URLs:
            // 1. If we have imageFileIds from metadata (created by backend from base64), use them FIRST
            // 2. If we have attachmentFilenames from metadata (sent by backend), use sandbox-image endpoint
            // 3. Otherwise, extract filename from src and use sandbox-image endpoint as fallback
            if (src?.startsWith('attachment:')) {
              const filename = src.replace('attachment:', '');
              
              if (allImageFileIds.length > 0 && allImageFileIds[0]) {
                // Priority 1: Use the file_id created by backend from base64 image content
                imgSrc = `${getApiBaseUrl()}/api/files/${allImageFileIds[0]}`;
                console.log(`🖼️ Found attachment image with imageFileId: ${src} -> ${imgSrc} (using file_id from metadata)`);
              } else if (allAttachmentFilenames.length > 0) {
                // Priority 2: Use attachment filenames from metadata (backend detected attachment: references)
                // Find matching filename or use first one
                const matchingFilename = allAttachmentFilenames.find(f => f === filename) || allAttachmentFilenames[0];
                const sandboxUrl = conversationId 
                  ? `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(matchingFilename)}?conversation_id=${encodeURIComponent(conversationId)}`
                  : `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(matchingFilename)}`;
                imgSrc = sandboxUrl;
                console.log(`🖼️ Found attachment image: ${filename} -> ${imgSrc} (using attachment_filenames from metadata)`);
              } else {
                // Priority 3: Fallback - extract filename from src and use sandbox-image endpoint
                const sandboxUrl = conversationId 
                  ? `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(filename)}?conversation_id=${encodeURIComponent(conversationId)}`
                  : `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(filename)}`;
                imgSrc = sandboxUrl;
                console.log(`🖼️ Found attachment image: ${filename} -> ${imgSrc} (using sandbox-image endpoint - fallback)`);
              }
            }
            // Handle sandbox:/mnt/data/ URLs (graphiques Code Interpreter)
            else if (src?.startsWith('sandbox:/mnt/data/') || src?.startsWith('/mnt/data/')) {
              const filename = src.replace(/^(sandbox:)?\/mnt\/data\//, '');
              // Priorité: image_file_ids (graphiques) > sandbox-image > file_ids
              if (allImageFileIds.length > 0 && allImageFileIds[0]) {
                imgSrc = `${getApiBaseUrl()}/api/files/${allImageFileIds[0]}`;
                console.log(`🖼️ Sandbox image: ${src} -> ${imgSrc} (image_file_id)`);
              } else if (conversationId) {
                imgSrc = `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(filename)}?conversation_id=${encodeURIComponent(conversationId)}`;
                console.log(`🖼️ Sandbox image: ${src} -> ${imgSrc} (sandbox-image fallback)`);
              } else if (imageEligibleFileIds.length > 0) {
                imgSrc = `${getApiBaseUrl()}/api/files/${imageEligibleFileIds[0]}`;
                console.log(`🖼️ Sandbox image: ${src} -> ${imgSrc} (file_id fallback, excluding CSV)`);
              } else {
                imgSrc = src;
                console.warn('⚠️ Sandbox image URL found but no file IDs or conversationId:', src);
              }
            }
            
            return (
              <img
                src={imgSrc}
                alt={alt}
                {...props}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  marginTop: '8px',
                  marginBottom: '8px',
                  ...props.style
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  const retryCount = parseInt(target.dataset.imgRetry || '0', 10);
                  const maxRetries = 3;
                  if (retryCount >= maxRetries) {
                    console.warn('❌ Markdown image failed after retries, hiding:', imgSrc);
                    target.style.display = 'none';
                    target.onerror = null;
                    return;
                  }
                  target.dataset.imgRetry = String(retryCount + 1);
                  console.error('❌ Markdown image failed to load:', imgSrc, `(retry ${retryCount + 1}/${maxRetries})`);
                  
                  let nextSrc: string | null = null;
                  if (retryCount === 0) {
                    if (src?.startsWith('attachment:')) {
                      const filename = src.replace('attachment:', '');
                      nextSrc = conversationId 
                        ? `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(filename)}?conversation_id=${encodeURIComponent(conversationId)}`
                        : `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(filename)}`;
                    } else {
                      const attachmentMatch = content.match(/attachment:([^\s\)]+\.(png|jpg|jpeg|gif))/i);
                      if (attachmentMatch && conversationId) {
                        nextSrc = `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(attachmentMatch[1])}?conversation_id=${encodeURIComponent(conversationId)}`;
                      }
                    }
                  }
                  if (retryCount <= 1 && !nextSrc && (src?.startsWith('sandbox:/mnt/data/') || src?.startsWith('/mnt/data/'))) {
                    const filename = src.replace(/^(sandbox:)?\/mnt\/data\//, '');
                    if (conversationId) {
                      nextSrc = `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(filename)}?conversation_id=${encodeURIComponent(conversationId)}`;
                    }
                  }
                  if (retryCount <= 1 && !nextSrc && allImageFileIds.length > 0 && allImageFileIds[0]) {
                    nextSrc = `${getApiBaseUrl()}/api/files/${allImageFileIds[0]}`;
                  }
                  if (!nextSrc && imageEligibleFileIds.length > 0) {
                    nextSrc = `${getApiBaseUrl()}/api/files/${imageEligibleFileIds[0]}`;
                  }
                  if (nextSrc) {
                    console.log(`🔄 Retrying image load: ${nextSrc}`);
                    target.src = nextSrc;
                  } else {
                    target.style.display = 'none';
                    target.onerror = null;
                  }
                }}
              />
            );
          },
          
          // Custom code block with better overflow handling
          pre: ({ children, ...props }: any) => (
            <pre
              {...props}
              style={{
                overflow: 'auto',
                maxWidth: '100%',
                ...props.style
              }}
            >
              {children}
            </pre>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </StyledMarkdown>
  );
};

export default MarkdownMessage;
